import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { Gauge, Activity, AlertCircle } from 'lucide-react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import * as ss from 'simple-statistics';
import { ParsedWorksheet } from './worksheet';

interface CapabilityControlChartsProps {
  worksheet: ParsedWorksheet;
  selectedColumnId?: string;
}

const formatNumber = (value: number) => (Number.isFinite(value) ? value.toFixed(4) : '—');

export const CapabilityControlCharts: React.FC<CapabilityControlChartsProps> = ({ worksheet, selectedColumnId }) => {
  const defaultColumnId = selectedColumnId ?? worksheet.numericColumns[0]?.id ?? '';
  const [metricColumnId, setMetricColumnId] = useState(defaultColumnId);
  const [lsl, setLsl] = useState('10');
  const [usl, setUsl] = useState('14');

  const metricColumn = worksheet.numericColumns.find((column) => column.id === metricColumnId) ?? worksheet.numericColumns[0];
  const data = metricColumn?.numericValues ?? [];

  const capability = useMemo(() => {
    if (data.length < 2) return null;

    const mean = ss.mean(data);
    const sigma = ss.standardDeviation(data);
    const sampleSigma = ss.sampleStandardDeviation(data);
    const lowerSpec = Number(lsl);
    const upperSpec = Number(usl);

    if (!Number.isFinite(lowerSpec) || !Number.isFinite(upperSpec) || lowerSpec >= upperSpec || sigma === 0 || sampleSigma === 0) {
      return null;
    }

    const cp = (upperSpec - lowerSpec) / (6 * sampleSigma);
    const cpu = (upperSpec - mean) / (3 * sampleSigma);
    const cpl = (mean - lowerSpec) / (3 * sampleSigma);
    const cpk = Math.min(cpu, cpl);
    const pp = (upperSpec - lowerSpec) / (6 * sigma);
    const ppk = Math.min((upperSpec - mean) / (3 * sigma), (mean - lowerSpec) / (3 * sigma));
    const ppmOutside = (data.filter((value) => value < lowerSpec || value > upperSpec).length / data.length) * 1_000_000;

    const min = Math.min(...data);
    const max = Math.max(...data);
    const binCount = Math.max(6, Math.ceil(Math.sqrt(data.length)));
    const binWidth = (max - min || 1) / binCount;
    const histogram = Array.from({ length: binCount }, (_, index) => {
      const binStart = min + index * binWidth;
      const binEnd = index === binCount - 1 ? max + Number.EPSILON : binStart + binWidth;
      const count = data.filter((value) => value >= binStart && value < binEnd).length;
      return {
        label: `${binStart.toFixed(2)}–${(binStart + binWidth).toFixed(2)}`,
        count,
        midpoint: binStart + binWidth / 2,
      };
    });

    return {
      mean,
      sigma,
      sampleSigma,
      cp,
      cpu,
      cpl,
      cpk,
      pp,
      ppk,
      lowerSpec,
      upperSpec,
      ppmOutside,
      histogram,
    };
  }, [data, lsl, usl]);

  const individualsChart = useMemo(() => {
    if (data.length < 2) return null;

    const movingRanges = data.slice(1).map((value, index) => Math.abs(value - data[index]));
    const movingRangeBar = ss.mean(movingRanges);
    const estimatedSigma = movingRangeBar / 1.128;
    const mean = ss.mean(data);
    const individualUcl = mean + 3 * estimatedSigma;
    const individualLcl = mean - 3 * estimatedSigma;
    const mrUcl = movingRangeBar * 3.267;

    return {
      individuals: data.map((value, index) => ({
        sample: index + 1,
        value,
        center: mean,
        ucl: individualUcl,
        lcl: individualLcl,
      })),
      movingRanges: movingRanges.map((value, index) => ({
        sample: index + 2,
        value,
        center: movingRangeBar,
        ucl: mrUcl,
        lcl: 0,
      })),
      mean,
      movingRangeBar,
      individualUcl,
      individualLcl,
      mrUcl,
      outOfControlPoints: data.filter((value) => value > individualUcl || value < individualLcl).length,
    };
  }, [data]);

  if (worksheet.numericColumns.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>No numeric worksheet columns</AlertTitle>
        <AlertDescription>Paste worksheet data with at least one numeric measurement column to run capability analysis and control charts.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-rose-600 p-3 text-white">
          <Gauge size={24} />
        </div>
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Badge>Ready</Badge>
            <Badge variant="outline">Capability + SPC</Badge>
          </div>
          <h2 className="text-2xl font-semibold text-foreground">Capability + Control Charts</h2>
          <p className="text-muted-foreground">Analyze process spread against specifications and monitor stability with an Individuals / Moving Range chart.</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Process setup</CardTitle>
          <CardDescription>Select the measurement column and enter lower / upper specification limits.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <Label>Measurement column</Label>
            <Select value={metricColumnId} onValueChange={setMetricColumnId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {worksheet.numericColumns.map((column) => (
                  <SelectItem key={column.id} value={column.id}>
                    {column.name} ({column.numericCount})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Lower specification limit</Label>
            <Input value={lsl} onChange={(event) => setLsl(event.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Upper specification limit</Label>
            <Input value={usl} onChange={(event) => setUsl(event.target.value)} />
          </div>
        </CardContent>
      </Card>

      {!capability || !individualsChart ? (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Need valid specifications and measurement variation</AlertTitle>
          <AlertDescription>Make sure the selected column has at least two values and the lower limit is less than the upper limit.</AlertDescription>
        </Alert>
      ) : (
        <Tabs defaultValue="capability" className="space-y-4">
          <TabsList>
            <TabsTrigger value="capability">Capability</TabsTrigger>
            <TabsTrigger value="control">Control charts</TabsTrigger>
          </TabsList>

          <TabsContent value="capability" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
              {[
                ['Cp', capability.cp],
                ['Cpk', capability.cpk],
                ['Pp', capability.pp],
                ['Ppk', capability.ppk],
                ['Mean', capability.mean],
                ['PPM out', capability.ppmOutside],
              ].map(([label, value]) => (
                <Card key={label}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{label}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-xl font-semibold">{formatNumber(Number(value))}</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Capability histogram</CardTitle>
                <CardDescription>Visualize the process distribution against your entered specification window.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={capability.histogram}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" minTickGap={24} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="hsl(var(--primary))" />
                      <Scatter data={[{ midpoint: capability.lowerSpec, count: 0 }]} fill="hsl(var(--destructive))" />
                      <Scatter data={[{ midpoint: capability.upperSpec, count: 0 }]} fill="hsl(var(--destructive))" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4 text-sm">
                  <div className="rounded-lg border p-3">CPU: {formatNumber(capability.cpu)}</div>
                  <div className="rounded-lg border p-3">CPL: {formatNumber(capability.cpl)}</div>
                  <div className="rounded-lg border p-3">σ (sample): {formatNumber(capability.sampleSigma)}</div>
                  <div className="rounded-lg border p-3">Specs: {formatNumber(capability.lowerSpec)} to {formatNumber(capability.upperSpec)}</div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="control" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Individuals center line</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-xl font-semibold">{formatNumber(individualsChart.mean)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Individuals UCL</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-xl font-semibold">{formatNumber(individualsChart.individualUcl)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Moving range bar</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-xl font-semibold">{formatNumber(individualsChart.movingRangeBar)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Out-of-control points</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-xl font-semibold">{individualsChart.outOfControlPoints}</div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg"><Activity size={18} /> Individuals chart</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={individualsChart.individuals}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="sample" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} dot />
                      <Line type="monotone" dataKey="center" stroke="hsl(var(--muted-foreground))" dot={false} strokeDasharray="6 6" />
                      <Line type="monotone" dataKey="ucl" stroke="hsl(var(--destructive))" dot={false} strokeDasharray="4 4" />
                      <Line type="monotone" dataKey="lcl" stroke="hsl(var(--destructive))" dot={false} strokeDasharray="4 4" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Moving range chart</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={individualsChart.movingRanges}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="sample" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="value" stroke="hsl(var(--chart-2))" strokeWidth={2} dot />
                      <Line type="monotone" dataKey="center" stroke="hsl(var(--muted-foreground))" dot={false} strokeDasharray="6 6" />
                      <Line type="monotone" dataKey="ucl" stroke="hsl(var(--destructive))" dot={false} strokeDasharray="4 4" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};
